import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import _ from "lodash";

// Zustand store
import { useAppStore } from "../../stores";

// Import Generic components
import {
  saveAttendee
} from "./services";

import { getAllCredentialTypes } from "../../actions/CredentialActions/CredentialsActions";

import { useSnackbar } from "notistack";

// import generic components
import ConventionRegistrationForm from "components/ConventionRegistrationForm/ConventionRegistrationForm";
import GenericSpinner from "../../components/GenericSpinner/GenericSpinner";

// import permissions enum
import { usertype_permissions } from "../../enums/PermissionEnums";

interface Props {
  member?: unknown;
}

// Custom styles
const regloadingStyle = {
  position: "fixed" as const,
  zIndex: 500,
  top: 0,
  backgroundColor: "rgba(0,0,0, .25)",
  left: 0,
  height: "100%",
  width: "100%",
};

const GuestRegistration: React.FC<Props> = () => {
  const { enqueueSnackbar } = useSnackbar();

  const navigate = useNavigate();

  // Zustand store
  const permissions = useAppStore((state) => state.user?.permissions || []);
  const creds = useAppStore(
    (state) => state.credentials?.credentialTypes || []
  );
  const setCredentialTypes = useAppStore((state) => state.setCredentialTypes);
  const setCommitteeTypes = useAppStore((state) => state.setCommitteeTypes);
  const attendeeSavePending = useAppStore(
    (state) => state.attendee?.attendeeSavePending || false
  );
  const attendeeLoadPending = useAppStore(
    (state) => state.attendee?.attendeeLoadPending || false
  );
  const saveAttendeeError = useAppStore(
    (state) => state.attendee?.attendeeError
  );
  const setSaveAttendeePending = useAppStore((state) => state.setSaveAttendeePending);
  const setSaveAttendeeSuccess = useAppStore((state) => state.setSaveAttendeeSuccess);
  const setSaveAttendeeError = useAppStore((state) => state.setSaveAttendeeError);

  const isAdmin =
    _.find(permissions, (permission: any) => {
      return (
        permission.name.indexOf(usertype_permissions.Admin) !== -1 ||
        permission.name.indexOf(usertype_permissions.BackOffice) !== -1 ||
        permission.name.indexOf(usertype_permissions.AffiliatePresident) !== -1 ||
        permission.name.indexOf(usertype_permissions.StateFed) !== -1 ||
        permission.name.indexOf(usertype_permissions.Designee) !== -1
      );
    }) !== undefined;

  const isMember =
    _.find(permissions, (permission: any) => {
      return permission.name.indexOf(usertype_permissions.Member) !== -1;
    }) !== undefined;

  const isAffiliateStaff =
    _.find(permissions, (permission: any) => {
      return (
        permission.name.indexOf(usertype_permissions.AffiliateStaff) !== -1
      );
    }) !== undefined;

  const saveAttendeeForUserCard = (responseData: any) => {
    if (_.get(responseData, "attendeeId") != 0) {
      // Always set credentialType from the list based on credentialTypeId to ensure consistency
      const credType = _.find(creds, function (cred: any) {
        return (
          String(cred.credentialTypeId) ===
          String(responseData.credentialTypeId)
        );
      });
      responseData.credentialType = credType ? (credType as any).label : "";
    }
    // Return the enriched data
    return responseData;
  };

  const [isNewReservation, setIsNewReservation] = React.useState(false);

  const [
    notificationRequestPending,
    setNotificationRequestPending,
  ] = React.useState(false);

  const [
    isRegisteredForConvention,
    setIsRegisteredForConvention,
  ] = React.useState(false);

  const [hasHotelReservation, setHasHotelReservation] = React.useState(false);

  const [
    isCredentialTypeRequestPending,
    setIsCredentialTypeRequestPending,
  ] = React.useState(false);

  // get all of the credential types
  React.useEffect(() => {
    if (creds.length == 0) {
      setIsCredentialTypeRequestPending(true);
      getAllCredentialTypes()
        .then((response) => {
          if (response.data) {
            const { data } = response;
            setCredentialTypes(data);
            setIsCredentialTypeRequestPending(false);
          }
        })
        .catch((error) => {
          console.log(error);
          setIsCredentialTypeRequestPending(false);
        });
    }
  }, []);



  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);

  const handleFormSubmit = (ev: React.SyntheticEvent<HTMLFormElement>) => {
    if(ev) ev.preventDefault();
    setSaveAttendeePending(true);
    saveAttendee(attendeeRecord).then(
      (response) => {
        const { data } = response;

        setIsRegisteredForConvention(true);
        setSaveAttendeeSuccess(data.result);
        // Enrich the data with credential type label and update global store
        const enrichedData = saveAttendeeForUserCard(data.result);
        setAttendeeRecord(enrichedData);
        enqueueSnackbar(data.messages.join(""), { variant: "success" });
        
        // Trigger success view
        setShowRegistrationSuccess(true);
        
        if (isNewReservation && (isMember || isAffiliateStaff)) {
          navigate("/hotel-reservation");
        } else if (isNewReservation && isAdmin) {
          navigate("/convention-registration");
        }
      },
      (error) => {
        console.log(error);
        setSaveAttendeeSuccess(null);
        setSaveAttendeeError(error);
        enqueueSnackbar("Error: " + error.response.data.messages, {
          variant: "error",
        });
      }
    );
  };

  // This one is binded to the input fields
  const [attendeeRecord, setAttendeeRecord] = useState<any>({
    firstName: "",
    lastName: "",
    contactEmail: "",
    phone: "",
    address1: "",
    address2: "",
    city: "",
    stateCode: "",
    zip: "",
    localNumber: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    committeeChoice1: "",
    committeeChoice2: "",
    committeeChoice3: "",
    alternatePriority: "",
    credentialTypeId: "",
    repAffiliateGuid: "",
    homeAffiliateGuid:"",
    individualGuid:"",
    retireeAttendee: false,
    attendeeId: 0,
    affiliate: null,
    parentAffiliate: null,
    memberId: "",
    interpretorNeeded: false,
    interpretorLanguage: "",
    reportedDate: "",
    createdDate: null,
    modifiedDate: null,
  });

  // Use global attendee record instead of local state for savedAttendeeRecord to get credential type updates
  const savedAttendeeRecord = useAppStore((state) => state.attendee?.attendeeRecord) || {
    ...attendeeRecord,
  };

  const [
    conventionRegistrationErrors,
    setConventionRegistrationErrors,
  ] = React.useState({
    phone: { error: false },
    emergencyContactPhone: { error: false },
    zip: { error: false },
    contactEmail: { error: false },
    ccEmail : {error:false}
  });

  const handleFormChange = (ev: any) => {
    let newState = { ...attendeeRecord };

    const { name, value, checked, type } = ev.target;

    if (type === "checkbox") {
      newState[name] = checked;

      // clear dropdown if it is the interpretor checkbox
      if (name === "interpretorNeeded" && checked === false) {
        newState["InterpretorLanguage"] = "";
      }

      setAttendeeRecord({
        ...newState,
      });
      return;
    } else if (type === "text") {
      let pattern: RegExp | null = null;

      if (name === "phone" || name === "emergencyContactPhone") {
        pattern = /^\d{10}$/g;
      }
      if (name === "zip") {
        pattern = /^\d{5}$/g;
      }
      if (name === "contactEmail") {
        pattern = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,63})+$/;
      }
      if (name === "ccEmail") {
        pattern = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,63})+$/;
      }

      let isError = false;
      if (name === "phone" || name === "emergencyContactPhone") {
        if (pattern && !value.match(pattern)) {
          if (value.length !== 0) {
            isError = true;
          }
        }
      } else {
        isError = pattern ? !value.match(pattern) : false;
      }

      setConventionRegistrationErrors({
        ...conventionRegistrationErrors,
        [name]: { error: isError },
      });
    }

    newState[ev.target.name] = ev.target.value;
    setAttendeeRecord({
      ...newState,
    });
  };

  const additionalFields = () => {
    return "";
  };

  const credentialsDisplay = () => {

    if(attendeeRecord.credentialTypeId!="13"){
      setAttendeeRecord({...attendeeRecord, credentialTypeId:"13" });
    }
    var credential = _.find(creds, (cred: any) => {
      return cred.credentialTypeId == attendeeRecord.credentialTypeId;
    });
    if (credential) {
      return <div>Credential Type: {(credential as any).label}</div>;
    }
  };

  const repRadioDisplay = () => {
    return null;
  };

  return (
    <div>
      {
        <div>
          {(attendeeLoadPending || attendeeSavePending) && (
            <div style={regloadingStyle}>
              <GenericSpinner includeOverlay={true} padding={25} />
            </div>
          )}
          <ConventionRegistrationForm
            conventionRegistrationFormErrors={conventionRegistrationErrors}
            handleFormChange={handleFormChange}
            handleFormSubmit={handleFormSubmit}
            additionalFields={additionalFields()}
            credentialsDisplay={credentialsDisplay()}
            attendeeRecord={attendeeRecord}
            savedAttendeeRecord={savedAttendeeRecord}
            repRadioDisplay={repRadioDisplay()}
            isNewReservation={true}
            notificationRequestPending={notificationRequestPending}
            isRegisteredForConvention={isRegisteredForConvention}
            hasHotelReservation={hasHotelReservation}
            isCredentialTypeRequestPending={isCredentialTypeRequestPending}
            ShouldCertifiedPopupShowBasedOnCertificationType={false}
            showSuccessView={showRegistrationSuccess}
            submissionError={saveAttendeeError}
            onEditRegistration={() => setShowRegistrationSuccess(false)}
            onCancelEdit={() => {
              setAttendeeRecord({ ...savedAttendeeRecord });
              setShowRegistrationSuccess(true);
            }}
          />
        </div>
      }
    </div>
  );
};

export default GuestRegistration;
